import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import * as date from '@owl-nest/date'
import * as api from '@owl-nest/api-client/latest'
import * as model from '@owl-nest/models'

import * as styles from '../../../style'

import { Supporter } from '../../../../../models/supporter'
import * as projectModel from '../../../../../models/project'
import { formatBacker } from '../../../utils/formatBacker'
import { TRACKING_SOURCE } from '../../../utils/trackerEnums'
import { DonationBasedContent, RewardBasedContent } from '../../../components/Backer'

type BackerPropTypes = {
  supporter: api.Supporter
  project: projectModel.Project
  variant?: string
  source: TRACKING_SOURCE
}

export function Backer({ supporter, project, variant, source }: BackerPropTypes): React.ReactElement<BackerPropTypes> {
  const { dateOrder, labelColor, labelName } = formatBacker(supporter, project)

  const isOwnerComment = supporter.id === project.owner.id

  return (
    <styles.BackerContainer variant={variant}>
      {/* Avatar */}
      <styles.Avatar href={supporter.absolute_url} variant={variant}>
        <plume.styles.image.Avatar src={model.user.avatar(supporter, '55x55')} type="medium" />
      </styles.Avatar>
      <BackerInfo variant={variant}>
        {/* Name */}
        {supporter.is_anonymous ? (
          <plume.styles.heading.XS>{t('Anonymous backer')}</plume.styles.heading.XS>
        ) : (
          <plume.styles.heading.XS as="a" href={supporter.absolute_url}>
            {model.user.fullName(supporter)}
          </plume.styles.heading.XS>
        )}
        {/* Label */}
        {labelName && <plume.RoleTag backgroundColor={labelColor}>{labelName}</plume.RoleTag>}
        <br />
        <BackerContent>
          <plume.styles.copy.M>
            {project.type === api.ProjectType.Donation ? (
              <DonationBasedContent project={project} supporter={supporter} />
            ) : (
              <RewardBasedContent project={project} source={source} supporter={supporter} />
            )}
          </plume.styles.copy.M>
          {/* Comments */}
          {!isOwnerComment && supporter.latest_project_comment && supporter.latest_project_comment?.is_public && (
            <styles.BackerCommentWrapper>{supporter.latest_project_comment.comment}</styles.BackerCommentWrapper>
          )}
        </BackerContent>
        {/* Last contribution date */}
        {dateOrder && (
          <plume.styles.heading.XXXXXS>
            {date.formatDistanceToNow(dateOrder, { includeSeconds: true })}
          </plume.styles.heading.XXXXXS>
        )}
      </BackerInfo>
    </styles.BackerContainer>
  )
}

const BackerInfo = styled(styles.BackerInfo)`
  flex-wrap: nowrap;
  justify-content: space-between;

  @media screen and ${plume.BREAKPOINTS.MOBILE_L} {
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;

    ${plume.styles.heading.XXXXXS} {
      margin-left: auto;
    }
  }
`

const BackerContent = styled.div`
  order: 0;
  width: 100%;

  ${plume.styles.copy.M} {
    ${plume.Link} {
      display: inline;
    }
  }

  ${plume.styles.copy.S} {
    margin-top: 10px;
  }

  @media screen and ${plume.BREAKPOINTS.MOBILE_L} {
    order: 1;
  }
`
